import React from 'react'
import { Box, alpha, useTheme } from '@mui/material'
import VeraLogo from 'assets/images/logo.svg'
import { authPx } from '../authScale'

export interface AuthLogoProps {
  /** Pulses the mark while an auth request is in flight. */
  isLoading?: boolean
}

/**
 * The VERA wordmark shown above an auth form (sign-in today; sign-up is a
 * pending redesign — see the sign-up page's commented-out header).
 *
 * Extracted on its second use (VERA-1095, house rule: extract on the second
 * hand-roll) — sign-in had this hand-rolled as a bare `<Box component="img">`
 * with no link and no keyboard semantics; sign-up currently renders no logo
 * at all. Pulled out now so sign-up can adopt the same clickable treatment
 * later without re-deriving it.
 *
 * Links out to the marketing site (matches the destination + new-tab
 * convention already used by `apps/app`'s `PoweredByVera` attribution) so a
 * user mid-sign-in who clicks the mark doesn't lose their typed email/OTP
 * state in this tab.
 */
export const AuthLogo = ({ isLoading }: AuthLogoProps) => {
  const theme = useTheme()

  return (
    <Box
      component="a"
      href="https://www.vera.inc"
      target="_blank"
      rel="noopener noreferrer"
      aria-label="VERA. Opens vera.inc in a new tab."
      // The ambient halo centres itself on this element (SignInBackground,
      // option B, 2026-09-23). Every auth page renders this logo, at a
      // different height, so the background measures it rather than guessing.
      data-auth-logo=""
      sx={{
        display: 'inline-block',
        lineHeight: 0,
        borderRadius: '4px',
        outlineOffset: '4px',
        '&:focus-visible': {
          outline: `2px solid ${alpha(theme.palette.text.primary, 0.6)}`,
        },
      }}
    >
      <Box
        component="img"
        src={VeraLogo}
        alt="VERA"
        sx={{
          width: authPx(140),
          height: 'auto',
          // The source SVG is BLACK. `invert(1)` is what makes it legible on a
          // dark backdrop — so it is exactly wrong on a light one, where the
          // mark should be left as authored. Conditional rather than a filter
          // that happens to be a no-op: there is no single filter string that
          // is correct in both modes, because the two want opposite ends of the
          // ramp from one source asset.
          filter: theme.palette.mode === 'dark' ? 'brightness(0) invert(1)' : 'none',
          display: 'block',
          ...(isLoading && {
            '@keyframes veraLoaderPulse': {
              '0%, 100%': { opacity: 0.45, transform: 'scale(1)' },
              '50%': { opacity: 1, transform: 'scale(1.04)' },
            },
            animation: 'veraLoaderPulse 1.6s ease-in-out infinite',
            '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.85 },
          }),
        }}
      />
    </Box>
  )
}

export default AuthLogo
